// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import VideoAsset, { VideoAssetsStyles } from "@conklin/App/Components/Media/VideoAsset";
import xUseOverlayBadge from "@conklin/App/Hooks/xUseOverlayBadge";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { getSelectedImageOrVideo } from "@conklin/App/Store/Selectors/ProductSelectors";
import { getSettingsCollection } from "@conklin/App/Store/Selectors/SettingsSelectors";
import IconProdListingsZoom from "@conklin/XDL/Icons/IconProdListingsZoom";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { DocumentModel, ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductImageCarouselStyles } from "@insite/content-library/Components/ProductImageCarousel";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import Badge from "@insite/mobius/Badge";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconMemo } from "@insite/mobius/Icon";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    selectedImage: getSelectedImage(state, ownProps.productContext.product),
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
    // XNG-Change
    customSettings: getSettingsCollection(state).customSettings,
    selectedView: getSelectedImageOrVideo(state, ownProps.productContext.product),
});

const mapDispatchToProps = {
    setSelectedImageIndex,
};

export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;

    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Change
    wrapper?: InjectableCss;
    primaryImageWrapper?: InjectableCss;
    videoContainer?: InjectableCss;
    modalImageContainer?: InjectableCss;
    videoStyles?: VideoAssetsStyles;
    enlargeBtn?: ClickablePresentationProps;
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                // XNG-Changes: CMI-713
                // max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-height: 70vh;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const styles = primaryImageStyles;

const ProductDetailsPrimaryImage = ({
    productContext,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
    // XNG-Changes:
    selectedView,
}: Props) => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);
    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
    };

    const { product } = productContext;
    if (!product) {
        return null;
    }

    const view =
        selectedView ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const hasMediaPath =
        (view as DocumentModel).filePath || (view as ImageModel).mediumImagePath || (view as ImageModel).largeImagePath;

    if (!hasMediaPath) {
        return null;
    }

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
        // XNG-Changes - xUseOverlayBadge
    } = xUseOverlayBadge({
        productContext,
        showTextBadge: true,
        showImageBadge: true,
        styles,
        isPrev: true,
    });

    // const path = image.mediumImagePath || image.largeImagePath;
    // if (!path) {
    //     return null;
    // }

    return (
        <StyledWrapper {...styles.wrapper}>
            <>
                {!showOverlayBadge && (
                    <StyledWrapper {...styles.primaryImageWrapper}>
                        {(view as DocumentModel).filePath ? (
                            <>
                                <VideoAsset
                                    type="Vimeo"
                                    name={view.name}
                                    url={(view as DocumentModel).filePath}
                                    isThumbnailClickable
                                    showThumbnail={true}
                                    extendedStyles={styles.videoStyles}
                                />
                                <Clickable
                                    {...styles.enlargeBtn}
                                    onClick={imageClickHandler}
                                    data-test-selector="productDetails-image-zoom"
                                    aria-label="zoom icon"
                                >
                                    <Icon src={IconProdListingsZoom} size={16} color="common.ui5" />
                                </Clickable>
                            </>
                        ) : (
                            <ProductPrimaryImage
                                product={product}
                                image={view as ImageModel}
                                onClick={imageClickHandler}
                                extendedStyles={styles.primaryImage}
                                {...{ useZoom: true }}
                            />
                        )}
                    </StyledWrapper>
                )}
                {showOverlayBadge && overlayBadge && (
                    <StyledWrapper {...styles.badgeAreaWrapper} ref={badgeAreaWrapperRef}>
                        {(view as DocumentModel).filePath ? (
                            <>
                                <VideoAsset
                                    type="Vimeo"
                                    name={view.name}
                                    url={(view as DocumentModel).filePath}
                                    isThumbnailClickable
                                    showThumbnail={true}
                                    extendedStyles={styles.videoStyles}
                                />
                                <Clickable
                                    {...styles.enlargeBtn}
                                    onClick={imageClickHandler}
                                    data-test-selector="productDetails-image-zoom"
                                    aria-label="zoom icon"
                                >
                                    <Icon src={IconProdListingsZoom} size={16} color="common.ui5" />
                                </Clickable>
                            </>
                        ) : (
                            <ProductPrimaryImage
                                product={product}
                                image={view as ImageModel}
                                onClick={imageClickHandler}
                                extendedStyles={styles.primaryImage}
                                {...{ useZoom: true }}
                            />
                        )}

                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </>

            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                {(view as DocumentModel).filePath ? (
                    <VideoAsset
                        type="Vimeo"
                        name={view.name}
                        url={(view as DocumentModel).filePath}
                        isThumbnailClickable
                        showThumbnail={true}
                        extendedStyles={styles.videoStyles}
                    />
                ) : (
                    <ProductPrimaryImage
                        product={product}
                        image={view as ImageModel}
                        useLargeImage={true}
                        extendedStyles={styles.fullPrimaryImage}
                    />
                )}

                {/* XNG-Change not included in invision */}

                {/* {product.images && product.images.length > 1 && (
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        onSelectImage={selectImageHandler}
                        extendedStyles={styles.imageCarousel}
                    />
                )} */}
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
